<template>
  <basic-portlet :title="form_title" pre-caption="Timezone:"
                 caption-style="font-weight: bold; font-size: bigger"
                 :caption="timezone">
    <template #portlet_actions>
      <b-button id="template-fill-btn" variant="ss-standout" @click="selectTemplate">
        <i class="la la-tasks" /> Fill from template
      </b-button>
    </template>
    <b-row v-if="is_template">
      <b-col cols="6">
        <b-form-group label="Template name:" label-for="template-name">
          <input v-model="template_name" class="form-control" type="text">
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="6">
        <b-row>
          <b-col sm="12" md="7"
                 lg="5"
                 class="mr-auto">
            <display-with-selection-modal :preloaded-artworks="artworks"
                                          :preselected-artwork="event_data.image"
                                          @selected="(ev) => (event_data.image = ev.selected_artwork)" />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="6">
        <b-form-group label="Event name:" label-for="event-name">
          <input id="event-name" ref="event-name"
                 v-model="event_data.name"
                 placeholder="Enter a name..."
                 type="text"
                 class="form-control">
        </b-form-group>
        <b-form-group label="Description:" label-for="event-description">
          <textarea id="event-description" v-model="event_data.description"
                    class="form-control"
                    placeholder="Enter a description..." />
        </b-form-group>
      </b-col>
      <b-col cols="6">
        <b-form-group label="Source stream:" label-for="event-stream">
          <multiselect id="event-stream" v-model="event_data.stream"
                       :allow-empty="false"
                       deselect-label="An event must have a stream"
                       placeholder="Select a stream..."
                       :options="streams"
                       track-by="name" label="name" />
        </b-form-group>
        <b-form-group label="Event tags:" label-for="event-tags">
          <multiselect v-model="ms_tag_values"
                       :options="auto_complete_tags"
                       :multiple="true"
                       :taggable="true"
                       placeholder="Search or add new tags..."
                       track-by="uuid"
                       label="name"
                       @tag="addNewTag" />
        </b-form-group>
      </b-col>
    </b-row>
    <hr>
    <b-alert variant="ss-primary" :show="true">
      <i class="flaticon2-warning" /><span style="font-weight: bold;">Timezone:&nbsp;</span> <span> {{ timezone }} </span>
    </b-alert>
    <b-row>
      <b-col cols="6">
        <b-form-group label="Event type:" label-for="event-type">
          <toggle-switch id="event-type" v-model="event_data.is_recurring"
                         :block="false"
                         left-label="One-off"
                         right-label="Recurring" />
        </b-form-group>
      </b-col>
      <b-col v-if="event_data.is_recurring" cols="6">
        <b-form-group label="Recur lifetime:" label-for="event-recur-forever">
          <toggle-switch id="event-recur-forever"
                         v-model="event_data.recur_forever"
                         :block="false"
                         left-label="Manual" right-label="Forever"
                         @change="toggleRecurForever" />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="6">
        <b-form-group label="Event start:" label-for="event-start">
          <datetime id="event-start" v-model="event_data.start_date_time"
                    :auto="true" :value-zone="timezone"
                    :zone="timezone"
                    :min-datetime="min_date_time_start"
                    input-class="form-control"
                    type="datetime" :format="date_format"
                    @close="startDateTimeClose" />
        </b-form-group>
        <b-form-group v-if="!event_data.is_recurring" label="Publish start:" label-for="event-publish-start">
          <datetime id="event-publish-start" v-model="event_data.publish_start_date_time"
                    :auto="true" :value-zone="timezone"
                    :zone="timezone"
                    :min-datetime="min_publish_date_time_start" input-class="form-control"
                    type="datetime" :format="date_format"
                    @close="publishStartDateTimeClose" />
        </b-form-group>
        <template v-else>
          <b-form-group v-show="! event_data.recur_forever" label="Recur from:" label-for="event-recur-begin">
            <datetime id="event-recur-begin" v-model="event_data.recur_from"
                      :auto="true" :value-zone="timezone"
                      :zone="timezone"
                      :min-datetime="min_begin_from_date"
                      input-class="form-control"
                      type="datetime" :format="date_format"
                      @close="beginFromDateTimeClose" />
          </b-form-group>
          <duration-picker v-model="event_data.publish_delay" label="Publish delay:" />

          <b-form-group label="Frequency:" label-for="event-frequency">
            <multiselect v-model="event_data.frequency_selected" :options="[`daily`, `weekly`]" />
          </b-form-group>
        </template>
      </b-col>
      <b-col cols="6">
        <b-form-group label="Event end:" label-for="event-stop">
          <datetime id="event-stop"
                    v-model="event_data.stop_date_time"
                    :auto="true"
                    :value-zone="timezone"
                    :zone="timezone"
                    :min-datetime="min_date_time_stop"
                    :max-datetime="max_date_time_stop"
                    input-class="form-control"
                    type="datetime" :format="date_format" />
        </b-form-group>
        <b-form-group v-if="!event_data.is_recurring" label="Publish stop:" label-for="event-publish-stop">
          <datetime id="event-publish-stop"
                    v-model="event_data.publish_stop_date_time"
                    :auto="true"
                    :value-zone="timezone"
                    :zone="timezone"
                    :min-datetime="min_publish_date_time_stop"
                    input-class="form-control"
                    type="datetime" :format="date_format" />
        </b-form-group>
        <template v-else>
          <b-form-group v-show="! event_data.recur_forever" label="Recur until:" label-for="event-recur-end">
            <datetime id="event-recur-end"
                      v-model="event_data.recur_until"
                      :auto="true"
                      :value-zone="timezone"
                      :zone="timezone"
                      :min-datetime="min_end_before_date"
                      :max-datetime="max_end_before_date"
                      input-class="form-control"
                      type="datetime"
                      :format="date_format" />
          </b-form-group>
          <duration-picker v-model="event_data.publish_duration" :seconds="true" label="Publish duration:" />
          <b-form-group v-if="event_data.frequency_selected === `weekly`">
            <checkbox-group id="selected-days" v-model="selected_days" :options="day_options" />
          </b-form-group>
        </template>
      </b-col>
    </b-row>
    <b-form-group>
      <template v-if="!is_template">
        <b-button variant="ss-standout" :disabled="validation_errs.has_errs" @click="saveEvent">
          <i class="la la-save" /> Save event
        </b-button>
        <b-button variant="ss" :disabled="validation_errs.has_errs" @click="saveAsTemplate">
          <i class="la la-tasks" /> Save as template
        </b-button>
      </template>
      <template v-else>
        <b-button variant="ss-standout" :disabled="validation_errs.has_errs" @click="updateTemplate">
          <i class="la la-save" /> Save changes
        </b-button>
      </template>
    </b-form-group>
    <b-alert variant="ss" :show="validation_errs.no_stream">
      <i class="flaticon2-warning" />Event must have a source stream.
    </b-alert>
    <b-alert variant="ss" :show="validation_errs.no_name">
      <i class="flaticon2-warning" />Event must have a name.
    </b-alert>
    <b-alert variant="ss" :show="validation_errs.weekly_days">
      <i class="flaticon2-warning" />Weekly event needs at least one selected day.
    </b-alert>
    <entity-selection-modal name="entity-selection-modal"
                            :entities="templates"
                            entity-type="Template"
                            ms-track-by="id"
                            ms-label="name" />
    <save-as-template-modal />
    <quick-alert-modal v-bind="quick_alert" />
  </basic-portlet>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BAlert,
  VBToggle,
} from 'bootstrap-vue';
import { Datetime } from "vue-datetime";
import camelCase from 'lodash/camelCase'; // ...
import snakeCase from 'lodash/snakeCase';
import Ladda from "ladda";
import 'ladda/dist/ladda-themeless.min.css';

import BasicPortlet from 'VueComponents/BasicPortlet';
import ToggleSwitch from 'VueComponents/ToggleSwitch';
import EntitySelectionModal from 'VueModals/EntitySelectionModal';
import SaveAsTemplateModal from 'VueModals/SaveAsTemplateModal';
import DurationPicker from 'VueComponents/DurationPicker';
import DisplayWithSelectionModal from "VuePartials/Artwork/DisplayWithSelectionModal";
import CheckboxGroup from 'VueComponents/CheckboxGroup';
import TaggableModelMixin from 'VueMixins/TaggableModelMixin';
import { durationObjToUnit } from 'Helpers/formatters';

const duration_obj_to_string = (dur_obj) => {
  const o = moment.duration(Object.entries(dur_obj).reduce((acc, [k, v]) => {
    if (k === `days`) acc += (1000 * 60 * 60 * 24) * v;
    else if (k === `hours`) acc += (1000 * 60 * 60) * v;
    else if (k === `minutes`) acc += (1000 * 60) * v;
    else if (k === `seconds`) acc += 1000 * v;
    return acc;
  }, 0)).asSeconds();
  return o;
};

export default {
  components: {
    DisplayWithSelectionModal,
    BasicPortlet,
    ToggleSwitch,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BAlert,
    Datetime,
    EntitySelectionModal,
    SaveAsTemplateModal,
    DurationPicker,
    CheckboxGroup,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  mixins: [TaggableModelMixin],
  props: {
    templateData: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data: () => ({
    selected_days: [],
    day_options: [
      {
        display: 'Mon',
      },
      {
        display: 'Tue',
      },
      {
        display: 'Wed',
      },
      {
        display: 'Thu',
      },
      {
        display: 'Fri',
      },
      {
        display: 'Sat',
      },
      {
        display: 'Sun',
      },
    ],
    timezone: window.user.timezone ? window.user.timezone : `UTC`,
    event_data: {
      is_recurring: false,
      recur_forever: false,
      name: '',
      image: null,
      stream: null,
      description: '',
      tags: [],
      start_date_time: moment().add(10, `minutes`).set({
        seconds: 0,
        milliseconds: 0,
      }).toISOString(),
      stop_date_time: moment().add(30, `minutes`).set({
        seconds: 0,
        milliseconds: 0,
      }).toISOString(),
      duration: null,
      frequency_selected: `daily`,
      days_selected: {
        MON: false,
        TUE: false,
        WED: false,
        THU: false,
        FRI: false,
        SAT: false,
        SUN: false,
      },
      publish_start_date_time: null,
      publish_stop_date_time: null,
      publish_delay: {},
      publish_duration: {
        seconds: window.preferences.scheduler_publish_duration,
      },
      recur_from: moment().subtract(5, `hours`).set({
        seconds: 0,
        milliseconds: 0,
      }).toISOString(),
      recur_until: moment().add(3, `months`).set({
        seconds: 0,
        milliseconds: 0,
      }).toISOString(),
    },
    template_name: null, // for if we want to rename it
  }),
  asyncComputed: {
    templates: {
      async get() {
        // wait for DOM load so Ladda has something to grab
        await new Promise(res => document.addEventListener("DOMContentLoaded", res));
        const btn_el = document.getElementById(`template-fill-btn`);
        const ladda_el = Ladda.create(btn_el);
        ladda_el.start();
        try {
          const res_data = (await axios.get(`/api/v1/templates`)).data.data;
          return res_data ?? [];
        } catch (err) {
          window.handleAxiosError(err);
          btn_el.disabled = true;
          return [];
        } finally {
          ladda_el.stop();
        }
      },
      default: [],
    },
    streams: {
      async get() {
        try {
          const res_data = (await axios.get(`/api/v1/streams`)).data.data.streams;
          return this.fallback(res_data, []);
        } catch (err) {
          window.handleAxiosError(err);
          return [];
        }
      },
      default: [],
    },
    artworks: {
      async get() {
        try {
          const res_data = (await axios.get(`/api/v1/artwork`))?.data?.data;
          return this.fallback(res_data, []);
        } catch (err) {
          window.handleAxiosError(err);
          return [];
        }
      },
      default: [],
    },
  },
  computed: {
    date_format() {
      return `dd-MM-yyyy HH:mm`;
    },
    validation_errs() {
      const errs = {
        weekly_days: this.event_data.frequency_selected === `weekly` && !this.selected_days.length,
        no_stream: this.nullish(this.event_data.stream),
        no_name: !this.event_data.name.length,
      };
      errs.has_errs = Object.values(errs).some(err => err === true);
      return errs;
    },
    is_template() {
      return !this.nullish(this.templateData);
    },
    plaintext_type() {
      return this.is_template ? `template` : `event`;
    },
    form_title() {
      if (this.is_template) {
        return `Editing template: ${this.fallback(this.templateData?.name, ``)}`;
      } else {
        return `Creating new event`;
      }
    },
    min_date_time_start() {
      return moment()
        .add(2, "minutes")
        .set({
          seconds: 0,
          milliseconds: 0,
        })
        .toISOString();
    },
    // minimum stop is 1 min longer than start
    min_date_time_stop() {
      return moment(this.event_data.start_date_time)
        .add(3, "minutes")
        .set({
          seconds: 0,
          milliseconds: 0,
        })
        .toISOString();
    },
    // maximum stop can be 24h
    max_date_time_stop() {
      return moment(this.event_data.start_date_time)
        .add(24, "hours")
        .set({
          seconds: 0,
          milliseconds: 0,
        })
        .toISOString();
    },
    min_publish_date_time_start() {
      return moment(this.event_data.stop_date_time).toISOString();
    },
    // minimum publish stop is 1 min longer than publish start
    min_publish_date_time_stop() {
      return moment(this.event_data.publish_start_date_time)
        .add(1, "minutes")
        .set({
          seconds: 0,
          milliseconds: 0,
        })
        .toISOString();
    },
    min_begin_from_date() {
      return moment(this.event_data.start_date_time)
        .subtract(5, "hours")
        .set({
          seconds: 0,
          milliseconds: 0,
        })
        .toISOString();
    },

    min_end_before_date() {
      return moment(this.event_data.begin_from_date)
        .add(1, "days")
        .set({
          seconds: 0,
          milliseconds: 0,
        })
        .toISOString();
    },

    max_end_before_date() {
      return moment(this.event_data.begin_from_date)
        .add(101, "years")
        .set({
          seconds: 0,
          milliseconds: 0,
        })
        .toISOString();
    },

    event_duration() {
      return moment.duration(moment(this.event_data.stop_date_time).diff(this.event_data.start_date_time)).asMinutes();
    },
    publish_duration() {
      const current_duration = this.event_data.publish_duration;

      if (!this.falsy(current_duration)) {
        return duration_obj_to_string(current_duration);
      }
      const start = this.event_data.publish_start_date_time;
      const stop = this.event_data.publish_stop_date_time;
      if (!this.falsy(stop)) {
        return moment.duration(moment(stop).diff(moment(start))).asMinutes();
      }
      return NaN;
    },
    payload() {
      const payload = Object.entries(this.event_data).reduce((acc, [k, v]) => ({
        ...acc,
        [camelCase(k)]: v,
      }), {});
      // annoying exceptions
      payload.duration = this.event_duration;
      payload.daysSelected = this.selected_days.map(day => day.toUpperCase());
      payload.publishDelay = !this.nullish(this.event_data.publish_delay) ? duration_obj_to_string(this.event_data.publish_delay) : null;
      payload.publishDuration = (() => {
        if (!this.nullish(this.event_data.publish_duration)) {
          if (typeof this.event_data.publish_duration === `number`) {
            return this.event_data.publish_duration;
          }
          return this.durationObjToUnit(this.event_data.publish_duration);
        }
        return 0;
      })();
      payload.stream = this.event_data.stream?.name;
      payload.image = this.event_data.image?.path;
      payload.tags = this.ms_tag_values.map(tag => tag.name);
      return payload;
    },
  },

  beforeMount() {
    // check for url params if any are set (from Calendar)
    const url_params = new URLSearchParams(window.location.search);
    const date_param = url_params.get('date');
    const start_param = url_params.get('start');
    const end_param = url_params.get('end');

    if (date_param) {
      const date_param_array = date_param.split("-");
      this.event_data.start_date_time = moment()
        .add(10, `minutes`).set({
          'year': date_param_array[0],
          'month': date_param_array[1] - 1,
          'date': date_param_array[2],
          seconds: 0,
          milliseconds: 0,
        }).toISOString();

      this.event_data.stop_date_time = moment(this.event_data.start_date_time).add(30, `minutes`).set({
        seconds: 0,
        milliseconds: 0,
      }).toISOString();
    } else if (start_param && end_param) {
      this.event_data.start_date_time = moment.unix(start_param).toISOString();
      this.event_data.stop_date_time = moment.unix(end_param).toISOString();
    }
  },

  mounted() {
    if (this.is_template) {
      this.template_name = this.templateData.name;
      this.event_data = Object.entries(this.templateData.template).reduce((acc, [k, v]) => ({
        ...acc,
        [snakeCase(k)]: v,
      }), {});
      this.selected_days = this.templateData.template.daysSelected;
      this.waitForReady(this.$asyncComputed.streams, this.$asyncComputed.artworks).then(() => {
        this.event_data.image = this.artworks.find(art => art.path === this.event_data.image);
        this.event_data.stream = this.streams.find(stream => stream.name === this.event_data.stream);
      });
    }

    EventBus.$on('new_artwork_selected', (payload) => {
      this.event_data.image = payload;
    });

    this.fetchTags();
  },
  methods: {
    durationObjToUnit,
    // validation hooks
    startDateTimeClose() {
      const min = moment(this.event_data.start_date_time)
        .add(10, 'minutes')
        .set({
          seconds: 0,
          milliseconds: 0,
        })
        .toISOString();
      this.event_data.stop_date_time = min;
      if (moment(this.event_data.start_date_time).diff(this.event_data.recur_from) > 0) {
        this.event_data.recur_from = moment(min).subtract(5, 'hours').subtract(10, 'minutes').toISOString();
      }
      if (moment(this.event_data.recur_from).diff(this.event_data.recur_until) > 0) {
        this.event_data.recur_until = moment(min).add(2, 'years').subtract(5, 'hours').toISOString();
      }
    },
    publishStartDateTimeClose() {
      if (moment(this.event_data.publish_start_date_time).diff(this.event_data.publish_stop_date_time) > 0) {
        this.event_data.publish_stop_date_time = moment(this.event_data.publish_start_date_time).add(1, 'minutes').toISOString();
      }
    },
    beginFromDateTimeClose() {
      if (moment(this.event_data.recur_from).diff(this.event_data.recur_until) > 0) {
        this.event_data.recur_until = moment(this.event_data.recur_from).add(2, 'years').toISOString();
      }
    },
    async selectTemplate() {
      const modal_res = await this.fireModal(`entity-selection-modal`);
      if (modal_res.data.selected_entity) {
        const the_data = modal_res.data.selected_entity.template;
        this.event_data = Object.entries(the_data).reduce((acc, [k, v]) => ({
          ...acc,
          [snakeCase(k)]: v,
        }), {});
        this.event_data.image = this.artworks.find(art => art.path === the_data.image);
        this.event_data.stream = this.streams.find(stream => stream.name === the_data.stream);
        this.$nextTick(() => {
          this.selected_days = the_data.daysSelected.map(day => `${day.charAt(0).toUpperCase()}${day.slice(1).toLowerCase()}`);
          this.$forceUpdate();
        });
      }
    },
    async updateTemplate() {
      try {
        await axios.patch(`/api/v1/templates/${this.templateData.id}`, {
          name: this.template_name,
          type: `recording`,
          template: this.payload,
        });
        toastr.success(`Successfully updated template ${this.template_name}!`);
      } catch (err) {
        window.handleAxiosError(err);
      }
    },
    async saveAsTemplate() {
      const modal_res = await this.fireModal('save-as-template');
      if (modal_res.data.success === true) {
        try {
          await axios.post(`/api/v1/templates`, {
            name: modal_res.data.name,
            type: `recording`,
            template: this.payload,
          });
          toastr.success(`Successfully saved template: ${modal_res.data.name}`);
        } catch (err) {
          toastr.error(`Error attempting to save as template.`);
          window.handleAxiosError(err);
        }
      }
    },
    async saveEvent() {
      // require at least 2 minutes in advance to guarantee start
      if (moment(this.payload.startDateTime).isBefore(moment().add(2, "minutes"))) {
        this.quickAlert({
          title: `Error`,
          message: `Event start must be at least 2 minutes in the future!`,
          variant: `error`,
        });
      } else {
        try {
          await axios.post(`/api/v1/scheduler`, {
            templateData: this.payload,
          });
          Swal.fire({
            title: 'Success!',
            text: `Event added: ${this.event_data.name}`,
            type: 'success',
            showCancelButton: true,
            cancelButtonText: 'Add Another',
            confirmButtonText: 'View Events',
          }).then((result) => {
            if (result.value) {
              // redurect to an event type you just created if it is not a calendar
              if (window.cookies.get('scheduler.index_tab') !== 'calendar') {
                if (this.is_recurring) {
                  window.cookies.set('scheduler.index_tab', 'recurring_events', '1y');
                } else {
                  window.cookies.set('scheduler.index_tab', 'one-off-events', '1y');
                }
              }
              window.location.href = '/scheduler';
            }
          });
        } catch (err) {
          window.handleAxiosError(err);
        }
      }
    },

    toggleRecurForever() {
      if (this.event_data.recur_forever) {
        this.event_data.recur_from = moment().toISOString();
        this.event_data.recur_until = moment().add(100, 'years').toISOString();
      }
    },

  },

};
</script>

<style lang="scss" scoped>

:deep(.form-group) {
  margin: 1.2em auto !important;
}

.btn-list {
  * {
    margin: 0 8px;
  }
}

.days-list {
  display: flex;
  margin: 1.5em auto;
  li {
    margin-right: 10px;
  }
}
</style>
